import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import Feature from "ol/Feature.js";
import { LineString, Point, Polygon } from "ol/geom.js";
import { Draw, Modify } from "ol/interaction.js";
import VectorLayer from "ol/layer/Vector.js";
import { Vector as VectorSource } from "ol/source";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RxCaretRight } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { dataAPI } from "../../api/axios";
import { setActiveCreateFeatureTool } from "../../redux/editingSlice";
import { setSnappingReady } from "../../redux/setConfigSlice";
import { findAddress } from "../Geocoding/geocodingUtil";
import MapContext from "../MapContext";
import { Snapping } from "../Markup/Snapping";
import { getMapLayerById } from "../utils/mapUtils";
import GPSTrimble from "./GPSTrimble";
import NewFeatureAttributes from "./NewFeatureAttributes";

const NewFeature = (prop) => {
    const dispatch = useDispatch();
    const parentInfo = prop.prop;
    const [points, setPoints] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [snapping, setSnapping] = useState(false);
    const [movedToAtt, setMovedToAtt] = useState(false);
    const [editVertexIndex, setEditVertexIndex] = useState(null);
    const { map } = useContext(MapContext);
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const layerVisProperties = useSelector(
        (state) => state.configData.layerVisProperties
    );
    const activeCreateFeatureTool = useSelector(
        (state) => state.editingOptions.activeCreateFeatureTool
    );
    const currentEditFeature = useSelector(
        (state) => state.editingOptions.currentEditFeature
    );
    const pointAddress = useRef();
    const canGoToAttr = useRef();

    useEffect(() => {
        if (points) {
            updateSketchLayer(points);
            if (
                parentInfo.selectedLayer.GeometryType === "Point" &&
                points.length === 1
            ) {
                findAddress(mapConfig, [points[0].X, points[0].Y], (rtv) => {
                    pointAddress.current = rtv;
                });
                canGoToAttr.current = true;
            } else if (
                ((parentInfo.selectedLayer.GeometryType === "Line" ||
                    parentInfo.selectedLayer.GeometryType === "Polyline") &&
                    points.length >= 2) ||
                (parentInfo.selectedLayer.GeometryType === "Polygon" &&
                    points.length >= 3)
            ) {
                canGoToAttr.current = true;
                pointAddress.current = "";
            } else {
                canGoToAttr.current = false;
                pointAddress.current = "";
            }
        }
    }, [points, mapConfig]);

    useEffect(() => {
        if (map) {
            addSketchLayer();
            addSnapSketchLayer();
            canGoToAttr.current = false;
        }
        return () => {
            map.removeLayer(getMapLayerById(map, "newFeatureSketchLayer"));
            map.removeLayer(getMapLayerById(map, "newFeatureSnapSketchLayer"));
        };
    }, [map]);
    const draw = useRef();
    const modifyInteract = useRef();
    const addInteraction = (tool, markupSource, markupSnappingSource) => {
        const drawType = tool;
        draw.current = new Draw({
            source: markupSnappingSource,
            type: drawType,
        });
        modifyInteract.current = new Modify({
            source: markupSource,
        });

        modifyInteract.current.on("modifyend", (e) => {
            let i = 0;
            if (e.features.array_[0].getGeometry().getType() === "Point") {
                let c = e.features.array_[0].getGeometry().getCoordinates();
                let a = [{ Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 }];
                setPoints(a);
            } else if (e.features.array_[0].getGeometry().getType() === "LineString") {
                let a = e.features.array_[0]
                    .getGeometry()
                    .getCoordinates()
                    .map((c) => {
                        return { Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 };
                    });
                setPoints(a);
            } else if (e.features.array_[0].getGeometry().getType() === "Polygon") {
                let a = e.features.array_[0]
                    .getGeometry()
                    .getCoordinates()
                    .flat()
                    .map((c) => {
                        return { Index: i++, X: c[0], Y: c[1], Z: c[2] ?? 0 };
                    });
                a = a.slice(0, a.length - 1);
                setPoints(a);
            }
        });
        map.addInteraction(draw.current);
        map.addInteraction(modifyInteract.current);
    };

    useEffect(() => {
        const markupSource = getMapLayerById(map, "newFeatureSketchLayer").getSource();
        const markupSnappingSource = getMapLayerById(
            map,
            "newFeatureSnapSketchLayer"
        ).getSource();

        if (activeCreateFeatureTool === "map") {
            //Add Map Click Interaction
            addInteraction("Point", markupSource, markupSnappingSource);
            if (snapping) {
                markupSnappingSource.on("addfeature", mapPointSingleClickSnapping);
                dispatch(setSnappingReady(true));
                map.un("singleclick", mapPointSingleClick);
                map.un("dblclick", mapPointDoubleClick);
            } else {
                map.on("singleclick", mapPointSingleClick);
                map.on("dblclick", mapPointDoubleClick);
                markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            }
        } else {
            //remove Map Click Interaction
            map.removeInteraction(draw.current);
            map.removeInteraction(modifyInteract.current);
            markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            map.un("singleclick", mapPointSingleClick);
            map.un("dblclick", mapPointDoubleClick);
        }
        setEditVertexIndex(null);
        return () => {
            map.removeInteraction(draw.current);
            map.removeInteraction(modifyInteract.current);
            markupSnappingSource.un("addfeature", mapPointSingleClickSnapping);
            map.un("singleclick", mapPointSingleClick);
            map.un("dblclick", mapPointDoubleClick);
        };
    }, [activeCreateFeatureTool, snapping]);

    const addSketchLayer = () => {
        let sketchLayer = new VectorLayer({
            source: new VectorSource(),
            properties: { Id: "newFeatureSketchLayer" },
        });
        sketchLayer.setZIndex(1001);
        map.addLayer(sketchLayer);
    };
    const addSnapSketchLayer = () => {
        let sketchLayer = new VectorLayer({
            source: new VectorSource(),
            properties: { Id: "newFeatureSnapSketchLayer" },
        });
        sketchLayer.setZIndex(1001);
        map.addLayer(sketchLayer);
    };
    const handleMapButton = () => {
        dispatch(setActiveCreateFeatureTool("map"));
    };
    const handleManualButton = () => {
        if (activeCreateFeatureTool === "manual") {
            dispatch(setActiveCreateFeatureTool("map"));
        } else {
            dispatch(setActiveCreateFeatureTool("manual"));
        }
    };
    const handleGPSButton = () => {
        if (activeCreateFeatureTool === "gps") {
            dispatch(setActiveCreateFeatureTool("map"));
        } else {
            dispatch(setActiveCreateFeatureTool("gps"));
        }
    };

    const gpsSetting = () => {
        console.log("GPS Setting");
    };

    const mapPointSingleClick = (e) => {
        let coor = map.getCoordinateFromPixel(map.getEventPixel(e.originalEvent));
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };
    const mapPointSingleClickSnapping = (e) => {
        const markupSource = getMapLayerById(
            map,
            "newFeatureSnapSketchLayer"
        ).getSource();
        markupSource.clear();
        let coor = e.feature.getGeometry().getCoordinates();
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };
    const mapPointDoubleClick = (e) => {
        let coor = map.getCoordinateFromPixel(map.getEventPixel(e.originalEvent));
        AddOrUpdateVertex(coor[0], coor[1], 0);
    };

    const gpsPoint = () => {
        AddOrUpdateVertex(
            Math.floor(Math.random() * (1510000 - 1450000) + 1450000),
            Math.floor(Math.random() * (690000 - 650000) + 650000),
            Math.floor(Math.random() * 50)
        );
    };

    const AddOrUpdateVertex = (X, Y, Z) => {
        setHasError(false);
        setErrorMsg("");
        if (movedToAtt) {
            return;
        }
        if (parentInfo.selectedLayer.GeometryType === "Point") {
            setPoints((current) => {
                return [{ Index: 0, X: X, Y: Y, Z: Z }];
            });
        } else {
            setPoints((current) => {
                return [...current, { Index: current.length, X: X, Y: Y, Z: Z }];
            });
        }
    };

    const updateSketchLayer = (pointsArr) => {
        const sketchLayer = getMapLayerById(map, "newFeatureSketchLayer");
        if (!sketchLayer) {
            return;
        }

        let style = new Style({
            fill: new Fill({
                color: "rgb(255, 0, 211)",
            }),
            stroke: new Stroke({
                color: "rgb(255, 158, 211)",
                width: 2,
            }),
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({
                    color: "rgb(255, 0, 211)",
                }),
            }),
        });

        sketchLayer.getSource().clear();

        pointsArr.forEach((point) => {
            let xVal = parseFloat(point.X);
            let yVal = parseFloat(point.Y);
            let zVal = parseFloat(point.Z);

            let sketch = new Feature(new Point([xVal, yVal, zVal]));
            sketch.setStyle(style);

            sketchLayer.getSource().addFeature(sketch);
        });

        if (
            parentInfo.selectedLayer.GeometryType === "Line" ||
            parentInfo.selectedLayer.GeometryType === "Polyline"
        ) {
            let vertices = points.map((i) => [i.X, i.Y, i.Z]);

            let sketchFeature = new Feature(new LineString([...vertices]));
            sketchFeature.setId("sketchLine");
            sketchFeature.setStyle(style);
            sketchLayer.getSource().addFeatures([sketchFeature]);
        }
        if (parentInfo.selectedLayer.GeometryType === "Polygon") {
            //map the vertices from states to a usable format
            let vertices = points.map((i) => [i.X, i.Y, i.Z]);
            //include the first Vertex at the end of the list to close polygon.
            let sketchFeature = new Feature(new Polygon([[...vertices, vertices[0]]]));
            sketchFeature.setId("sketchPolygon");
            sketchFeature.setStyle(style);
            sketchLayer.getSource().addFeatures([sketchFeature]);
        }
    };

    const onItemChange = (event) => {
        const editedItemID = event.dataItem.Index;
        const data = points.map((item) =>
            item.Index === editedItemID ? { ...item, [event.field]: event.value } : item
        );
        setPoints((current) => {
            return data;
        });
    };

    const rowClick = (event) => {
        if (activeCreateFeatureTool === "manual") {
            setEditVertexIndex(event.dataItem.Index);
        }
    };

    const rowDoubleClick = (event) => {
        let pArr = points.filter(function (obj) {
            return obj.Index !== event.dataItem.Index;
        });
        let index = 0;
        pArr.forEach((element) => {
            element.Index = index++;
        });
        setPoints((current) => {
            return pArr;
        });
    };
    const addRecord = () => {
        const newRecord = {
            Index: points.length + 1,
        };
        setPoints((current) => {
            return [...points, newRecord];
        });
        setEditVertexIndex(newRecord.Index);
    };
    const back = () => {
        parentInfo.returnToParent();
    };
    const toAttributes = () => {
        if (canGoToAttr.current) {
            dispatch(setActiveCreateFeatureTool(""));
            setMovedToAtt(true);
            setHasError(false);
            setErrorMsg("");
        } else {
            setHasError(true);
            setErrorMsg("Invalid Geometry");
        }
    };
    const returnFromChildHandler = (data) => {
        dispatch(setActiveCreateFeatureTool("map"));
        setMovedToAtt(false);
    };
    return (
        <>
            {(!movedToAtt || movedToAtt === false) && (
                <>
                    <Button onClick={back} className="back-button">
                        &lt;
                    </Button>
                    <h3 className="title">
                        Create {parentInfo.selectedLayer.DisplayName}
                    </h3>
                    <div className="edit-option">
                        <p>Select Entry Method</p>
                        <ButtonGroup>
                            <Button
                                togglable={true}
                                selected={
                                    activeCreateFeatureTool === "map" ? true : false
                                }
                                onClick={handleMapButton}>
                                Map
                            </Button>
                            <Button
                                togglable={true}
                                selected={
                                    activeCreateFeatureTool === "manual" ? true : false
                                }
                                onClick={handleManualButton}>
                                Manual
                            </Button>
                            <Button
                                disabled={true}
                                togglable={true}
                                selected={
                                    activeCreateFeatureTool === "gps" ? true : false
                                }
                                onClick={handleGPSButton}>
                                GPS
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className="edit-option">
                        <div className="edit-grid-header">
                            <div>Feature Coordinates:</div>
                            <Button
                                title="Add new"
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-tertiary attributes-button"
                                onClick={toAttributes}>
                                Attributes
                            </Button>
                        </div>
                        {hasError && <p className="edit-error">{errorMsg}</p>}
                        <Grid
                            editField="inEdit"
                            style={points.length > 6 ? { height: "30vh" } : {}}
                            data={points.map((item) => ({
                                ...item,
                                inEdit: item.Index === editVertexIndex,
                            }))}
                            onItemChange={onItemChange}
                            onRowClick={rowClick}
                            onRowDoubleClick={rowDoubleClick}>
                            {activeCreateFeatureTool === "manual" &&
                                (points.length === 0 ||
                                    parentInfo.selectedLayer.GeometryType !==
                                        "Point") && (
                                    <GridToolbar>
                                        <Button
                                            title="Add new"
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                            onClick={addRecord}>
                                            Add new
                                        </Button>
                                    </GridToolbar>
                                )}
                            {activeCreateFeatureTool === "gps" && (
                                <>
                                    <GridToolbar>
                                        {points.length === 1 &&
                                        parentInfo.selectedLayer.GeometryType ===
                                            "Point" ? (
                                            <Button
                                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-success"
                                                onClick={gpsPoint}>
                                                Update Point
                                            </Button>
                                        ) : (
                                            <Button
                                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                                onClick={gpsPoint}>
                                                Add Point
                                            </Button>
                                        )}
                                        <Button onClick={gpsPoint}>GPS Setting</Button>
                                    </GridToolbar>
                                </>
                            )}

                            <GridColumn
                                field="Index"
                                title="Index"
                                editor="numeric"
                                width="0"
                                editable={false}
                            />
                            <GridColumn field="X" title="X" editor="text" />
                            <GridColumn field="Y" title="Y" editor="text" />
                            <GridColumn field="Z" title="Z" editor="text" />
                        </Grid>
                    </div>
                    {activeCreateFeatureTool === "gps" && <GPSTrimble />}
                    {activeCreateFeatureTool === "map" && (
                        <div className="edit-option">
                            <Snapping setSnapping={setSnapping} snapping={snapping} />
                        </div>
                    )}
                </>
            )}
            {movedToAtt && movedToAtt === true && (
                <>
                    <NewFeatureAttributes
                        prop={{
                            selectedLayer: parentInfo.selectedLayer,
                            addressIn: pointAddress.current,
                            shape: points,
                            returnToParent: returnFromChildHandler,
                        }}
                    />
                </>
            )}
        </>
    );
};
export default NewFeature;

const drawStyle = new Style({
    zIndex: 1000,
    fill: new Fill({
        color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
        color: "rgba(0, 0, 0, 0.5)",
        width: 1.5,
    }),
    image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
        }),
        fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
        }),
    }),
});
